import React, { useState, useEffect } from 'react';
import { Save, Upload, X, RefreshCw, Image as ImageIcon, Palette } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';

interface BrandingConfigData {
  appName: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  darkModePrimaryColor: string;
}

export const BrandingConfig: React.FC = () => {
  const { config: currentConfig, refreshBranding } = useBranding();
  const [config, setConfig] = useState<BrandingConfigData>({
    appName: currentConfig.appName,
    logoUrl: currentConfig.logoUrl,
    faviconUrl: currentConfig.faviconUrl,
    primaryColor: currentConfig.primaryColor,
    secondaryColor: currentConfig.secondaryColor,
    accentColor: currentConfig.accentColor,
    successColor: currentConfig.successColor,
    warningColor: currentConfig.warningColor,
    errorColor: currentConfig.errorColor,
    darkModePrimaryColor: currentConfig.darkModePrimaryColor || ''
  });
  
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchBrandingConfig();
  }, []);

  const fetchBrandingConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setConfig({
            appName: data.data.appName,
            logoUrl: data.data.logoUrl,
            faviconUrl: data.data.faviconUrl,
            primaryColor: data.data.primaryColor,
            secondaryColor: data.data.secondaryColor,
            accentColor: data.data.accentColor,
            successColor: data.data.successColor,
            warningColor: data.data.warningColor,
            errorColor: data.data.errorColor,
            darkModePrimaryColor: data.data.darkModePrimaryColor || ''
          });
        }
      }
    } catch (error) {
      console.error('Failed to fetch branding config:', error);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Logo file size must be less than 2MB' });
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setLogoPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFaviconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 512 * 1024) {
      setMessage({ type: 'error', text: 'Favicon file size must be less than 512KB' });
      return;
    }

    setFaviconFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setFaviconPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config/logo`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      const data = await response.json();
      if (data.success) {
        setConfig(prev => ({ ...prev, logoUrl: data.data.logoUrl }));
        setLogoFile(null);
        setLogoPreview(null);
        setMessage({ type: 'success', text: 'Logo uploaded successfully' });
        await refreshBranding();
      } else {
        throw new Error(data.message || 'Failed to upload logo');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to upload logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleUploadFavicon = async () => {
    if (!faviconFile) return;

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', faviconFile);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config/favicon`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      const data = await response.json();
      if (data.success) {
        setConfig(prev => ({ ...prev, faviconUrl: data.data.faviconUrl }));
        setFaviconFile(null);
        setFaviconPreview(null);
        setMessage({ type: 'success', text: 'Favicon uploaded successfully' });
        await refreshBranding();
      } else {
        throw new Error(data.message || 'Failed to upload favicon');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to upload favicon' });
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config/logo`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        setConfig(prev => ({ ...prev, logoUrl: null }));
        setMessage({ type: 'success', text: 'Logo removed successfully' });
        await refreshBranding();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove logo' });
    }
  };

  const handleRemoveFavicon = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config/favicon`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        setConfig(prev => ({ ...prev, faviconUrl: null }));
        setMessage({ type: 'success', text: 'Favicon removed successfully' });
        await refreshBranding();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove favicon' });
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            appName: config.appName,
            primaryColor: config.primaryColor,
            secondaryColor: config.secondaryColor,
            accentColor: config.accentColor,
            successColor: config.successColor,
            warningColor: config.warningColor,
            errorColor: config.errorColor,
            darkModePrimaryColor: config.darkModePrimaryColor || undefined
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Branding configuration updated successfully' });
        await refreshBranding();
      } else {
        throw new Error(data.message || 'Failed to update configuration');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all branding to defaults? This will remove your custom logo and favicon.')) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/branding-config/reset`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (data.success) {
        await fetchBrandingConfig();
        setMessage({ type: 'success', text: 'Branding reset to defaults' });
        await refreshBranding();
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to reset branding' });
    } finally {
      setSaving(false);
    }
  };

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Branding Configuration
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Customize your application's branding, logo, and color scheme
        </p>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 text-success-800 dark:text-success-200'
              : 'bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 text-error-800 dark:text-error-200'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <ImageIcon className="w-5 h-5 mr-2 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">App Identity</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Application Name
              </label>
              <input
                type="text"
                value={config.appName}
                onChange={(e) => setConfig(prev => ({ ...prev, appName: e.target.value }))}
                maxLength={50}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="3PM Escrow"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                This name appears in the browser title and sidebar header
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <ImageIcon className="w-5 h-5 mr-2 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Logo Management</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Application Logo
              </label>
              
              {config.logoUrl && !logoPreview && (
                <div className="mb-4 flex items-center gap-4">
                  <div className="w-48 h-16 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center p-2">
                    <img
                      src={config.logoUrl.startsWith('http') ? config.logoUrl : `${apiUrl}${config.logoUrl}`}
                      alt="Current logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <button
                    onClick={handleRemoveLogo}
                    className="px-3 py-2 text-sm text-error-600 hover:text-error-700 dark:text-error-400 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              )}

              {logoPreview && (
                <div className="mb-4 flex items-center gap-4">
                  <div className="w-48 h-16 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center p-2">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setLogoFile(null);
                      setLogoPreview(null);
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadLogo}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploadingLogo ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              )}

              <div>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoChange}
                  className="hidden"
                  id="logo-upload"
                />
                <label
                  htmlFor="logo-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Logo File
                </label>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  PNG, JPG, or SVG. Max 2MB. Recommended: 200x60px
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <ImageIcon className="w-5 h-5 mr-2 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Favicon</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Browser Tab Icon
              </label>
              
              {config.faviconUrl && !faviconPreview && (
                <div className="mb-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center p-2">
                    <img
                      src={config.faviconUrl.startsWith('http') ? config.faviconUrl : `${apiUrl}${config.faviconUrl}`}
                      alt="Current favicon"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <button
                    onClick={handleRemoveFavicon}
                    className="px-3 py-2 text-sm text-error-600 hover:text-error-700 dark:text-error-400 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              )}

              {faviconPreview && (
                <div className="mb-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 flex items-center justify-center p-2">
                    <img
                      src={faviconPreview}
                      alt="Favicon preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setFaviconFile(null);
                      setFaviconPreview(null);
                    }}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUploadFavicon}
                    disabled={uploadingFavicon}
                    className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploadingFavicon ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload
                      </>
                    )}
                  </button>
                </div>
              )}

              <div>
                <input
                  type="file"
                  accept="image/png,image/x-icon"
                  onChange={handleFaviconChange}
                  className="hidden"
                  id="favicon-upload"
                />
                <label
                  htmlFor="favicon-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose Favicon File
                </label>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  ICO or PNG. Max 512KB. Recommended: 32x32px
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center mb-4">
            <Palette className="w-5 h-5 mr-2 text-primary-500" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Color Scheme</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Primary Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.primaryColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.primaryColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="#f97316"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Secondary Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.secondaryColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.secondaryColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="#ea580c"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Accent Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.accentColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, accentColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.accentColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, accentColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Success Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.successColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, successColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.successColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, successColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="#10b981"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Warning Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.warningColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, warningColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.warningColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, warningColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="#f59e0b"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Error Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.errorColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, errorColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.errorColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, errorColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="#ef4444"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Dark Mode Primary Color (Optional)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={config.darkModePrimaryColor || config.primaryColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, darkModePrimaryColor: e.target.value }))}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
                />
                <input
                  type="text"
                  value={config.darkModePrimaryColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, darkModePrimaryColor: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Leave empty to use primary color"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Override primary color for dark mode. Leave empty to use the same color as light mode.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <button
            onClick={handleResetToDefaults}
            disabled={saving}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Defaults
          </button>

          <button
            onClick={handleSaveConfig}
            disabled={saving}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
